/**
 * R6 — Briefing export modal. Per CD spec:
 *   - Format chips: Markdown (Slack) / PDF (stakeholders) / HTML (email, v1.5) /
 *     ICS (follow-ups, v1.5). v1 ships Markdown + PDF; HTML + ICS soon-tagged.
 *   - Preview renders the pinned + annotated items in document form, with:
 *     - Per-item source + asOf line (NON-NEGOTIABLE — operators downstream
 *       must be able to trace any number back to its source)
 *     - EDU disclaimer EMBEDDED in the preview body (not just chrome) so
 *       the export carries it through (per CD spec — disclaimer "embedded
 *       in the export itself")
 *   - "Diff vs yesterday" hint footer.
 */

import { useMemo, useState } from 'react';
import { FamilyGlyph, TierPip } from './primitives';
import type { PathogenFamily, ThreatTier } from './types';

type Format = 'markdown' | 'pdf' | 'html' | 'ics';

interface BriefingItem {
  common: string;
  admin: string;
  family: PathogenFamily;
  idc: string;
  tier: ThreatTier;
  metric: string;
  source: string;
  asOf: string;
  anno?: string;
}

const DEMO_ITEMS: BriefingItem[] = [
  {
    common: 'Ebola (Zaïre)', admin: 'North Kivu, DR Congo', family: 'haemorrhagic',
    idc: 'var(--bm-id-7)', tier: 1, metric: '142 cases · 63 deaths',
    source: 'WHO DON', asOf: '2026-05-29',
    anno: 'Cross-border risk into Uganda — ring vaccination starts Thu.',
  },
  {
    common: 'Mpox (clade I)', admin: 'Kinshasa, DR Congo', family: 'zoonotic',
    idc: 'var(--bm-id-4)', tier: 3, metric: '389 cases since onset',
    source: 'Africa CDC', asOf: '2026-05-28',
    anno: 'Watch for travel-linked exports.',
  },
  {
    common: 'Cholera', admin: 'Khartoum, Sudan', family: 'enteric',
    idc: 'var(--bm-id-3)', tier: 2, metric: '5,400 cases · coverage gap',
    source: 'WHO DON', asOf: '2026-05-28',
  },
];

export interface BriefingProps {
  onClose: () => void;
  /** Optional override of the demo items (production: pulled from pins +
   *  annotations server-side). */
  items?: BriefingItem[];
}

export function Briefing({ onClose, items: itemsOverride }: BriefingProps) {
  const [fmt, setFmt] = useState<Format>('markdown');
  const items = itemsOverride ?? DEMO_ITEMS;
  const extLabel = useMemo(() => fmt === 'markdown' ? '.md' : fmt === 'pdf' ? '.pdf' : fmt === 'html' ? '.html' : '.ics', [fmt]);

  return (
    <div style={{
      width: 'min(720px, 92vw)', maxHeight: '90vh',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bm-bg)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)',
      boxShadow: 'var(--bm-shadow-lg)',
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--bm-border)',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 680 }}>Briefing export</h2>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)', marginTop: 2 }}>
            {items.length} pinned + annotated items · editable before export
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          style={{
            background: 'transparent', border: '1px solid var(--bm-border)',
            color: 'var(--bm-text-muted)', borderRadius: 6,
            width: 28, height: 28, cursor: 'pointer', fontSize: 16, lineHeight: 1,
          }}
        >×</button>
      </header>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '10px 16px', borderBottom: '1px solid var(--bm-border)',
      }}>
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--bm-text-muted)' }}>
          Format
        </span>
        {([
          ['markdown', 'Markdown · Slack'],
          ['pdf', 'PDF · stakeholders'],
          ['html', 'HTML · email'],
          ['ics', 'ICS · follow-ups'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFmt(k)}
            style={{
              padding: '3px 9px', borderRadius: 999,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
              border: `1px solid ${fmt === k ? 'var(--bm-accent, #6ea3ff)' : 'var(--bm-border)'}`,
              background: fmt === k ? 'var(--bm-bg-subtle)' : 'transparent',
              color: fmt === k ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            {label}
            {(k === 'html' || k === 'ics') && (
              <span style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '.3px',
                padding: '0 4px', borderRadius: 3,
                background: 'var(--bm-bg-strong)', color: 'var(--bm-text-subtle)',
              }}>v1.5</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div style={{
          padding: 18,
          background: 'var(--bm-bg-subtle)',
          border: '1px solid var(--bm-border)',
          borderRadius: 'var(--bm-radius-sm)',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Daily situational briefing</div>
          <div style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)', marginBottom: 14 }}>
            {new Date().toISOString().slice(0, 10)} · {items.length} outbreaks · generated from "AFRO duty desk" watchlist
          </div>
          {/* EDU disclaimer EMBEDDED in the brief body — not just chrome */}
          <div style={{
            marginBottom: 14, padding: '8px 10px',
            border: '1px solid var(--bm-threat-3)55',
            background: 'var(--bm-threat-3-bg)',
            borderRadius: 'var(--bm-radius-sm)',
            fontSize: 11.5, color: 'var(--bm-threat-3)', fontWeight: 600,
          }}>
            ⚠ BioMax PaCC engineering prototype — EDU, NOT FOR OPERATIONAL DEPLOYMENT
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16, color: 'var(--bm-text-muted)' }}>
            <b style={{ color: 'var(--bm-text)' }}>Summary:</b>{' '}
            {items.filter(i => i.tier === 1).length} CRITICAL,{' '}
            {items.filter(i => i.tier === 2).length} HIGH,{' '}
            {items.filter(i => i.tier === 3).length} ELEVATED.
            {' '}Cross-border risk noted for Ebola.
          </div>
          {items.map((it, i) => (
            <article key={i} style={{
              padding: 10, marginBottom: 10,
              background: 'var(--bm-bg)',
              border: '1px solid var(--bm-border)',
              borderRadius: 'var(--bm-radius-sm)',
              display: 'flex', gap: 12,
            }}>
              <div style={{
                width: 60, height: 60, flexShrink: 0,
                display: 'grid', placeItems: 'center',
                background: 'var(--bm-bg-subtle)',
                border: '1px solid var(--bm-border)',
                borderRadius: 'var(--bm-radius-sm)',
                gap: 4,
              }}>
                <FamilyGlyph family={it.family} color={it.idc} size={20} />
                <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 8, color: 'var(--bm-text-subtle)' }}>
                  map snippet
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <TierPip tier={it.tier} small />
                  <b style={{ fontSize: 13.5 }}>{it.common}</b>
                </div>
                <div style={{ fontSize: 12, color: 'var(--bm-text-muted)', marginBottom: 3 }}>
                  {it.admin} · {it.metric}
                </div>
                <div style={{
                  fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
                }}>source: {it.source} · as of {it.asOf}</div>
                {it.anno && (
                  <div style={{
                    marginTop: 6, padding: '4px 8px',
                    background: 'var(--bm-anno-bg)',
                    border: '1px solid var(--bm-anno)55',
                    borderRadius: 3,
                    fontSize: 11.5, color: 'var(--bm-text)',
                  }}>📝 {it.anno}</div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>

      <footer style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: '10px 16px', borderTop: '1px solid var(--bm-border)',
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
        }}>
          every item carries source + asOf · disclaimer embedded · diff vs yesterday available
        </span>
        <button
          type="button"
          style={{
            padding: '6px 14px', borderRadius: 5, cursor: 'pointer',
            fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
            background: 'var(--bm-accent, #6ea3ff)',
            color: 'var(--bm-bg)',
            border: 'none',
          }}
        >Export {extLabel}</button>
      </footer>
    </div>
  );
}
